import { reducer as formReducer } from 'redux-form-5-stub';
import { reducer as formReducer2 } from 'redux-form';
import { combineReducers, Reducer, ReducersMapObject } from 'redux';
import { sortObject } from './sortReducers';
import { Auth } from './Auth/reducers';
import { BlockPageUI } from './BlockPageUI/reducers';
import { Blocks } from './Blocks/reducers';
import { Courses } from './Courses/reducers';
import { CourseMembership } from './CourseMembership/reducers';
import { CourseProgress } from './Courses/CourseProgress/reducers';
import { CourseTiles } from './Courses/CourseTiles/reducers';
import { ModuleProgress } from './ModuleProgress/reducers';
import { Modules } from './Modules/reducers';
import { CohortSchedules } from './CohortSchedules/reducers';
import { Cohorts } from './Cohorts/reducers';
import { CohortRoles } from './CohortRoles/reducers';
import { Enrolments } from './Enrolments/reducers';
import { FeedItems } from './FeedItems/reducers';
import { Feeds } from './Feeds/reducers';
import { Galleries } from './Galleries/reducers';
import { Groups } from './Groups/reducers';
import { GroupTypes } from './GroupTypes/reducers';
import { Institutions } from './Institutions/reducers';
import { InstitutionAdminUI } from './InstitutionAdminUI/reducers';
import { LTI } from './LTI/reducers';
import { Pages } from './Pages/reducers';
import { PaymentTransaction } from './PaymentTransaction/reducers';
import { MOLPayResponse } from './PaymentTransaction/MOLPay/reducers';
import { PlatformAnnouncements } from './PlatformAnnouncements/reducers';
import { Portal } from './Portal/reducers';
import { Portals } from './Portals/reducers';
import { PortalAdmin } from './PortalAdmin/reducers';
import { Posts } from './Posts/reducers';
import { Qualifications } from './Qualifications/reducers';
import { QualificationEnrolments } from './QualificationEnrolments/reducers';
import { Searches } from './Searches/reducers';
import { url } from './location/reducers';
import { Users } from './Users/reducers';
import { UserSuggest } from './Users/UserSuggest/reducers';
import {
  PageMode,
  StudentMode,
  CommentMode,
  changeMode,
  StudentProgressMode,
} from './Analytics/PageMode/reducers';
import { AnalyticsDetails } from './Analytics/reducers';
import { WidgetToolbox } from './WidgetToolbox/reducers';
import { WindowSize } from './WindowSize/reducers';
import { InstitutionExtras } from './InstitutionsExtras/reducers';
import { InstitutionReport } from './InstitutionsReports/reducers';
import { InstitutionEnrolments } from './InstitutionEnrolments/reducers';
import { NotificationEvents } from './NotificationEvents/reducers';
import { Subscriptions } from './Subscriptions/reducers';
import { Channels } from './Channels/reducers';
import { Comments } from './Comments/reducers';
import { CohortGroups } from './CohortGroups/reducers';
import { ProfilePage } from './ProfilePage/reducers';
import { CurrentCoursePortfolio } from './Courses/CurrentCoursePortfolio/reducers';
import { TopBar } from './TopBar/reducers';
import { ProgressPage } from './ProgressPage/reducers';
import { PageProgress } from './PageProgress/reducers';
import { PermissionGroups } from './PermissionGroups/reducers';
import { PageSettings } from './PageSettings/reducers';
import { Certification } from './Certification/reducers';
import { CertificationPathways } from './Certification/CertificationPathways/reducers';
import { SubscriptionPlans } from './SubscriptionPlans/reducers';
import { SubscribeModals } from './SubscriptionPlans/SubscribeModals/reducers';
import { UserSubscriptionPlans } from './UserSubscriptionPlans/reducers';
import { LoggedInUser } from './LoggedInUser/reducers';
import { CourseAppearanceSettings } from './CourseAppearanceSettings/reducers';
import { Optimizely } from './Optimizely/reducers';
import { ReduxState } from './state';
import { ReduxAction } from './actions';
import { StubReducer } from './reducer';
import { Userpilot } from './Userpilot/reducers';
import { InstitutionUsage } from './InstitutionUsage/reducers';
import { BlockMeta } from './Metas/BlockMeta/reducers';
import { BlockActionMeta } from './Metas/BlockActionMeta/reducers';
import { TempSharingSettings } from './TempSharingSettings/reducers';
import { Application } from './Application/slice';
import { InstitutionAdminSettings } from './InstitutionAdminSettings/slice';

/** DEPRECATION WARNING

 Unless a reducer needs to be everywhere, its better to include it in your root component like so:

 import { addReducer } from 'ReduxStorage/Store.jsx';
 import { SomeReducer } from 'ReduxStorage/SomeReducer/reducers.jsx';
 addReducer('SomeReducer', SomeReducer);

 This way it won't be included in chunks where it is not needed by any component

 Optionally you can enable hot module reloading for the reducer like so:

 if (module.hot) {
   module.hot.accept('ReduxStorage/SomeReducer/reducers.jsx', () => {
     addReducer('SomeReducer', SomeReducer);
   });
 }

*/

export const makeReducer = (
  routerReducer: StubReducer,
  injectedReducers: Partial<ReducersMapObject<ReduxState, ReduxAction>>
): Reducer<ReduxState> => {
  const reducers: ReducersMapObject<ReduxState, ReduxAction> = {
    AnalyticsDetails,
    Application,
    Auth,
    BlockPageUI,
    Blocks,
    changeMode,
    Channels,
    Certification,
    CertificationPathways,
    Cohorts,
    CohortGroups,
    CohortRoles,
    CommentMode,
    Comments,
    CourseAppearanceSettings,
    Courses,
    CourseMembership,
    CourseProgress,
    CourseTiles,
    CohortSchedules,
    CurrentCoursePortfolio,
    Enrolments,
    FeedItems,
    Feeds,
    form5: formReducer,
    form: formReducer2, // If you absolutely must mount it somewhere other than `form`, you may provide a getFormState(state) function to the reduxForm() decorator, to get the slice of the Redux state where you have mounted the redux-form reducer.
    Galleries,
    Groups,
    GroupTypes,
    InstitutionEnrolments,
    InstitutionAdminUI,
    InstitutionAdminSettings,
    InstitutionExtras,
    InstitutionReport,
    Institutions,
    InstitutionUsage,
    Modules,
    ModuleProgress,
    MOLPayResponse,
    LoggedInUser,
    LTI,
    NotificationEvents,
    Optimizely,
    PageMode,
    PageProgress,
    Pages,
    PageSettings,
    PaymentTransaction,
    PermissionGroups,
    PlatformAnnouncements,
    Portal,
    Portals,
    PortalAdmin,
    Posts,
    ProfilePage,
    ProgressPage,
    Subscriptions,
    Qualifications,
    QualificationEnrolments,
    Searches,
    StudentMode,
    StudentProgressMode,
    SubscribeModals,
    SubscriptionPlans,
    TempSharingSettings,
    TopBar,
    url,
    Users,
    UserSuggest,
    UserSubscriptionPlans,
    Userpilot,
    WidgetToolbox,
    WindowSize,
    BlockMeta,
    BlockActionMeta,
    location: routerReducer,
    ...injectedReducers,
  };
  sortObject(reducers);
  return combineReducers<ReduxState>(reducers);
};
